import React from 'react';
import { I18n } from 'react-redux-i18n';

import { I18N_BASE } from '../../constants';
import { DecisionType, DisagreementReason } from '../../models';
import CmRadioGroup from '../../components/primitives/CmRadioGroup';
import styles from './modals.module.css';

const REASONS: readonly DisagreementReason[] = [
  'aiMissedRedFlag',
  'aiFalseSignal',
  'contextOutsidePage',
  'edgeCaseNotInRubric',
  'borderline',
];

export type DisagreementMode = 'agreed' | 'required' | 'optional';

export interface DisagreementReasonRadioGroupProps {
  value: DisagreementReason | null;
  onChange: (next: DisagreementReason) => void;
  mode: DisagreementMode;
  decision: DecisionType;
  testId?: string;
}

export function DisagreementReasonRadioGroup({
  value,
  onChange,
  mode,
  decision,
  testId,
}: DisagreementReasonRadioGroupProps): React.ReactElement {
  // When the reviewer agrees with the AI's lean, no disagreement reason is
  // needed — render a greyed note saying so, not the radio group. This
  // signals "uncontroversial action" rather than asking for explanation.
  if (mode === 'agreed') {
    return (
      <div
        className={`${styles.section} ${styles.agreedNote}`}
        data-testid={testId}
        data-mode="agreed"
      >
        {I18n.t(`${I18N_BASE}.blockModal.disagreement.agreed`, { decision })}
      </div>
    );
  }

  return (
    <div className={styles.section} data-testid={testId} data-mode={mode}>
      <div className={styles.sectionTitle}>
        {I18n.t(
          mode === 'required'
            ? `${I18N_BASE}.blockModal.disagreement.required`
            : `${I18N_BASE}.blockModal.disagreement.optional`,
        )}
      </div>
      <CmRadioGroup
        name="disagreementReason"
        value={value}
        options={REASONS.map((reason) => ({
          value: reason,
          label: I18n.t(`${I18N_BASE}.disagreement.${reason}`),
        }))}
        onChange={onChange}
        testId={testId ? `${testId}-radio` : undefined}
      />
    </div>
  );
}

export default DisagreementReasonRadioGroup;
